/** Copyright (c) 2019 T. Rowe Price. All rights reserved. */

import './FundSelectionModal.scss';

import { array, func, object, string } from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';

import FundsList from 'components/layouts/Preferences/Subscriptions/FundsList';
import ActionButton from 'components/shared/Buttons/ActionButton';
import Checkbox from 'components/shared/Checkbox/Checkbox';
import BaseModal from 'components/shared/Modal/BaseModal';
import {
  getBroadAssetClassId,
  getBroadAssetClassName
} from 'services/Preferences';
import { groupMutualFunds } from 'state/preferences/PreferencesSelectors';

class FundSelectionModal extends React.Component {
  state = {
    assetClassChecked: {},
    broadAssetClassChecked: {},
    broadAssetClassNumAssetsChecked: {},
    broadAssetClassPartialChecked: {},
    broadAssetClassShowFunds: {}
  };

  componentDidMount() {
    const { fundsSelected, mutualFundGroups, mutualFunds } = this.props;
    const {
      assetClassChecked,
      broadAssetClassChecked,
      broadAssetClassNumAssetsChecked,
      broadAssetClassPartialChecked
    } = this.state;
    const { broadAssetClassIds, trpCodes } = fundsSelected;

    broadAssetClassIds.forEach((broadAssetClassId) => {
      const broadAssetClassName = getBroadAssetClassName(broadAssetClassId);
      const fund = mutualFundGroups.find(
        (group) => group.broadAssetClassName === broadAssetClassName
      );

      if (fund) {
        broadAssetClassChecked[broadAssetClassName] = true;
        const { listOfAssets } = fund;

        listOfAssets.forEach((asset) => {
          assetClassChecked[asset.name] = true;
        });
        broadAssetClassNumAssetsChecked[broadAssetClassName] =
          listOfAssets.length;
      }
    });
    trpCodes.forEach((trpCode) => {
      const fund = mutualFunds.find(
        (mutualFund) => mutualFund.trpCode === trpCode
      );
      if (fund) {
        const { broadAssetClassId, name } = fund;

        if (!broadAssetClassIds.includes(broadAssetClassId)) {
          const broadAssetClassName = getBroadAssetClassName(broadAssetClassId);

          assetClassChecked[name] = true;

          broadAssetClassChecked[broadAssetClassName] = true;

          broadAssetClassPartialChecked[broadAssetClassName] = true;

          const numAssetsChecked =
            broadAssetClassNumAssetsChecked[broadAssetClassName] || 0;
          broadAssetClassNumAssetsChecked[broadAssetClassName] =
            numAssetsChecked + 1;
        }
      }
    });

    this.setState({
      assetClassChecked,
      broadAssetClassChecked,
      broadAssetClassNumAssetsChecked,
      broadAssetClassPartialChecked
    });
  }

  toggleShowHideBroadAssetClassFunds = (broadAssetClassName) => () => {
    const { broadAssetClassShowFunds } = this.state;
    broadAssetClassShowFunds[broadAssetClassName] = Boolean(
      !broadAssetClassShowFunds[broadAssetClassName]
    );
    this.setState({ broadAssetClassShowFunds });
  };

  onDone = () => {
    const { mutualFundGroups, onClose, onDone } = this.props;
    const {
      assetClassChecked,
      broadAssetClassChecked,
      broadAssetClassPartialChecked
    } = this.state;

    const fundsSelected = {
      broadAssetClassIds: [],
      trpCodes: []
    };

    Object.entries(broadAssetClassChecked).forEach(
      ([broadAssetClassName, isChecked]) => {
        const isPartiallyChecked = Boolean(
          broadAssetClassPartialChecked[broadAssetClassName]
        );

        if (isChecked && !isPartiallyChecked) {
          const broadAssetClassId = getBroadAssetClassId(broadAssetClassName);
          fundsSelected.broadAssetClassIds.push(broadAssetClassId);
          const { listOfAssets } = mutualFundGroups.find(
            (group) => group.broadAssetClassName === broadAssetClassName
          );
          listOfAssets.forEach((asset) => {
            fundsSelected.trpCodes.push(asset.trpCode);
          });
        } else if (isChecked && isPartiallyChecked) {
          const { listOfAssets } = mutualFundGroups.find(
            (group) => group.broadAssetClassName === broadAssetClassName
          );
          listOfAssets.forEach((asset) => {
            const isChecked = Boolean(assetClassChecked[asset.name]);
            if (isChecked) {
              fundsSelected.trpCodes.push(asset.trpCode);
            }
          });
        }
      }
    );

    onDone(fundsSelected);
    onClose();
  };

  onBroadAssetClassCheckboxClick = (broadAssetClassName) => (checked) => {
    const { mutualFundGroups } = this.props;
    const {
      assetClassChecked,
      broadAssetClassChecked,
      broadAssetClassNumAssetsChecked,
      broadAssetClassPartialChecked
    } = this.state;

    broadAssetClassChecked[broadAssetClassName] = checked;
    broadAssetClassPartialChecked[broadAssetClassName] = false;

    const mutualFundGroup = mutualFundGroups.find(
      (group) => group.broadAssetClassName === broadAssetClassName
    );
    if (mutualFundGroup) {
      const { listOfAssets } = mutualFundGroup;
      if (checked) {
        listOfAssets.forEach((asset) => {
          assetClassChecked[asset.name] = true;
        });
        broadAssetClassNumAssetsChecked[broadAssetClassName] =
          listOfAssets.length;
      } else {
        listOfAssets.forEach((asset) => {
          assetClassChecked[asset.name] = false;
        });
        broadAssetClassNumAssetsChecked[broadAssetClassName] = 0;
      }
    }

    this.setState({
      assetClassChecked,
      broadAssetClassChecked,
      broadAssetClassNumAssetsChecked,
      broadAssetClassPartialChecked
    });
  };

  onAssetClassCheck = (broadAssetClassName) => (assetClassName, checked) => {
    const { mutualFundGroups } = this.props;
    const {
      assetClassChecked,
      broadAssetClassChecked,
      broadAssetClassNumAssetsChecked,
      broadAssetClassPartialChecked
    } = this.state;

    assetClassChecked[assetClassName] = checked;

    const { listOfAssets } = mutualFundGroups.find(
      (group) => group.broadAssetClassName === broadAssetClassName
    );
    const totalNumberOfAssets = listOfAssets.length;

    let numberAssetsChecked =
      broadAssetClassNumAssetsChecked[broadAssetClassName] || 0;

    if (checked) {
      numberAssetsChecked += 1;
    } else {
      numberAssetsChecked -= 1;
    }

    broadAssetClassNumAssetsChecked[broadAssetClassName] = numberAssetsChecked;

    if (numberAssetsChecked === 0) {
      broadAssetClassChecked[broadAssetClassName] = false;
      broadAssetClassPartialChecked[broadAssetClassName] = false;
    } else if (numberAssetsChecked === totalNumberOfAssets) {
      broadAssetClassChecked[broadAssetClassName] = true;
      broadAssetClassPartialChecked[broadAssetClassName] = false;
    } else {
      broadAssetClassChecked[broadAssetClassName] = true;
      broadAssetClassPartialChecked[broadAssetClassName] = true;
    }

    this.setState({
      assetClassChecked,
      broadAssetClassChecked,
      broadAssetClassNumAssetsChecked,
      broadAssetClassPartialChecked
    });
  };

  render() {
    const { mutualFundGroups, onClose, title } = this.props;

    const {
      assetClassChecked,
      broadAssetClassChecked,
      broadAssetClassPartialChecked,
      broadAssetClassShowFunds
    } = this.state;

    return (
      <BaseModal
        containerClassName="fund-selection-modal"
        hideAnchor={true}
        hideClose={true}
        isOpen={true}
        title={title}>
        <div className="fund-selection-header">Select Funds</div>
        <div className="broad-asset-class-rows">
          {mutualFundGroups.map(
            ({ broadAssetClassName, listOfAssets }, idx) => {
              const showFunds = broadAssetClassShowFunds[broadAssetClassName];
              const showHideText = `${showFunds ? 'Hide' : 'See'} all ${
                listOfAssets.length
              } Funds`;
              const isChecked = Boolean(
                broadAssetClassChecked[broadAssetClassName]
              );
              const isPartialChecked = Boolean(
                broadAssetClassPartialChecked[broadAssetClassName]
              );

              return (
                <div className="broad-asset-class-row" key={idx}>
                  <div className="broad-asset-class-row-heading">
                    <Checkbox
                      checked={isChecked}
                      className="broad-asset-class-checkbox"
                      label={broadAssetClassName}
                      name={`${broadAssetClassName}-checkbox`}
                      onChange={this.onBroadAssetClassCheckboxClick(
                        broadAssetClassName
                      )}
                      partial={isPartialChecked}
                    />
                    <div
                      className="show-hide-funds"
                      onClick={this.toggleShowHideBroadAssetClassFunds(
                        broadAssetClassName
                      )}>
                      {showHideText}
                    </div>
                  </div>
                  {showFunds && (
                    <FundsList
                      assetClassChecked={assetClassChecked}
                      funds={listOfAssets}
                      onCheck={this.onAssetClassCheck(broadAssetClassName)}
                    />
                  )}
                </div>
              );
            }
          )}
        </div>
        <div className="fund-selection-buttons">
          <ActionButton
            id="subscriptions-daily-fund-selection-cancel"
            className="fund-selection-button"
            onClick={onClose}
            type="secondary">
            Cancel
          </ActionButton>
          <ActionButton
            id="fund-selection-daily-subscriptions"
            className="fund-selection-button"
            onClick={this.onDone}
            type="primary">
            Done
          </ActionButton>
        </div>
      </BaseModal>
    );
  }
}

FundSelectionModal.propTypes = {
  fundsSelected: object.isRequired,
  mutualFunds: array.isRequired,
  mutualFundGroups: array.isRequired,
  onClose: func.isRequired,
  onDone: func.isRequired,
  title: string.isRequired
};

const mapStateToProps = (state) => ({
  mutualFunds: state.mutualFunds,
  mutualFundGroups: groupMutualFunds(state)
});

export default connect(mapStateToProps)(FundSelectionModal);
